import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import { Table } from 'reactstrap';
import Form from 'react-bootstrap/Form';
import BookCitation from '../../atoms/BookCitation/BookCitation';
import PhdAndMasterCitation from '../../atoms/PhdAndMastersCitation/PhdAndMastersCitation';
import Icon from '../../atoms/Icon/Icon';
import AudioCitation from '../../atoms/AudioCitation/AudioCitation';
import VideoCitation from '../../atoms/VideoCitation/VideoCitation';
import WebPageCitation from '../../atoms/WebPageCitation/WebPageCitation';
import Api from '../../../services/Api';
import FilterInput from '../../atoms/FilterInput/FilterInput';
import './CitationEditor.scss';

const citationDisplayMessage = (citation) => {
  switch (citation.type.name) {
    case 'Book':
      return `${citation.title} by ${citation.author || 'Unknown'}`;
    case 'Audio':
      return `${citation.title}`;
    case 'Video':
      return `${citation.title}`;
    case 'Paper':
      return `${citation.title} by ${citation.author || 'Unknown'}`;
    case 'Web Page':
      return `${citation.title} by ${citation.author || 'Unknown'}`;
    default:
      return citation.title;
  }
};

const citationIcon = (citation) => {
  switch (citation.type.name) {
    case 'Book':
      return 'book';
    case 'Audio':
      return 'file-audio';
    case 'Video':
      return 'file-video';
    case 'Paper':
      return 'file';
    case 'Web Page':
      return 'link';
    default:
      return 'file';
  }
};

const CitationEditor = ({
  onChange,
  initialCitations,
}) => {
  const [citationType, setCitationType] = useState('Book');
  const [citations, setCitations] = useState(initialCitations);
  const [isModalShown, setIsModalShown] = useState(false);
  const [citationsList, setCitationsList] = useState([]);
  const [filteredCitations, setFilteredCitations] = useState(undefined);
  const [filter, setFilter] = useState(undefined);
  const [isLoadingCitations, setIsLoadingCitations] = useState(false);
  const types = [
    'Book',
    'Paper',
    'Web Page',
    'PhD and Master',
  ];
  let currentCitationElement = <div />;
  switch (citationType) {
    case 'Audio':
      currentCitationElement = (
        <AudioCitation
          onRequestClose={() => setIsModalShown(false)}
          onChanged={(citation) => {
            setCitations([...citations, citation]);
            setIsModalShown(false);
          }}
        />
      );
      break;
    case 'Video':
      currentCitationElement = (
        <VideoCitation
          onRequestClose={() => setIsModalShown(false)}
          onChanged={(citation) => {
            setCitations([...citations, citation]);
            setIsModalShown(false);
          }}
        />
      );
      break;
    case 'Web Page':
      currentCitationElement = (
        <WebPageCitation
          onRequestClose={() => setIsModalShown(false)}
          onCitationAdded={(citation) => {
            setCitations([...citations, citation]);
            setIsModalShown(false);
          }}
        />
      );
      break;
    case 'PhD and Master':
      currentCitationElement = (
        <PhdAndMasterCitation
          onRequestClose={() => setIsModalShown(false)}
          onCitationAdded={(citation) => {
            setCitations([...citations, citation]);
            setIsModalShown(false);
          }}
        />
      );
      break;
    case 'Book':
    default:
      currentCitationElement = (
        <BookCitation
          onRequestClose={() => setIsModalShown(false)}
          onCitationAdded={(citation) => {
            setCitations([...citations, citation]);
            setIsModalShown(false);
          }}
        />
      );
      break;
  }
  useEffect(() => {
    const ids = citations.map(c => c.id);
    if (onChange) {
      onChange(ids);
    }
  }, [citations]);
  useEffect(() => {
    if (isLoadingCitations) {
      return;
    }
    setIsLoadingCitations(true);
    Api.get('types/citations?per_page=200').then((response) => {
      if (response.status === 200) {
        setCitationsList(response.data);
      }
    });
  }, [isLoadingCitations, citationsList]);
  useEffect(() => {
    if (!filter) {
      if (filteredCitations !== undefined) {
        setFilteredCitations(undefined);
      }
      return;
    }
    const items = citationsList.filter((x) => {
      const author = x.author && x.author.toLowerCase().startsWith(filter.toLowerCase());
      const publisher = x.publisher && x.publisher.toLowerCase().startsWith(filter.toLowerCase());
      const title = x.title && x.title.toLowerCase().startsWith(filter.toLowerCase());
      return author || publisher || title;
    });
    setFilteredCitations(items);
  }, [filter]);
  useEffect(() => {
    if (initialCitations && initialCitations.length > 0) {
      setCitations(initialCitations);
    }
  }, [initialCitations]);
  const removeCitation = (citation) => {
    const newList = citations.filter(c => c.id !== citation.id);
    setCitations(newList);
  };
  return (
    <div className="citation-editor">
      <Modal show={isModalShown} onHide={() => setIsModalShown(false)} size="lg" fullscreen>
        <Modal.Header closeButton>
          <Modal.Title>{`Add new citation [${citationType}]`}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex">
            <div style={{ width: '33%' }} className="border-right">
              <div className="row">
                <div className="col-md-12">
                  <label htmlFor="">Citation Type</label>
                  <Form.Select onChange={ev => setCitationType(types[ev.target.value - 1])}>
                    {types.map((type, index) => (
                      <option value={index + 1}>
                        {type === 'Paper' ? 'Research Paper' : type}
                      </option>
                    ))}
                  </Form.Select>
                </div>
              </div>
              <hr />
              <div className="p-3">
                {currentCitationElement}
              </div>
            </div>
            <div style={{ width: '67%' }} className="p-3">
              <div className="d-flex justify-content-end mb-4">
                <FilterInput
                  onDismiss={() => setFilter(undefined)}
                  onTriggered={value => setFilter(value)}
                />
              </div>
              <Table bordered hover striped>
                <colgroup>
                  <col width="5%" />
                  <col width="10%" />
                  <col width="80%" />
                  <col width="5%" />
                </colgroup>
                <thead>
                  <tr>
                    <th className="text-center">#</th>
                    <th>Type</th>
                    <th>Preview</th>
                    <th className="text-center">
                      <Icon icon="cogs" color="#000" />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(filteredCitations || citationsList).map(item => (
                    <tr
                      className="citation-row"
                      onClick={() => {
                        setCitations([...citations, item]);
                        setIsModalShown(false);
                      }}
                    >
                      <td className="text-center">{item.id}</td>
                      <td>{item.type.name}</td>
                      {['Audio', 'Video'].includes(item.type.name) && (
                        <td>
                          <span>File URL: </span>
                          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                          <a href="#">
                            {item.title}
                          </a>
                        </td>
                      )}
                      {['Book', 'Web Page', 'Paper'].includes(item.type.name) && (
                        <td>
                          <span>Title: </span>
                          <span className="text-info">{item.title}</span>
                          <span className="me-1">, </span>
                          <span>Author: </span>
                          <span className="text-info">{item.author}</span>
                          <span className="me-1">, </span>
                          <span>Language: </span>
                          <span className="text-info">{item.language || 'Unknown'}</span>
                          <span className="me-1">, </span>
                          <span>Publisher: </span>
                          <span className="text-info">{item.publisher || 'Unknown'}</span>
                          <span className="me-1">, </span>
                          <span>Edition: </span>
                          <span className="text-info">{item.edition || 'Unknown'}</span>
                          {item.phd ? (
                            <>
                              <span>PhD: </span>
                              <span className="text-info">{item.phd}</span>
                              <span className="me-1">, </span>
                            </>
                          ) : null}
                          {item.masters ? (
                            <>
                              <span>masters: </span>
                              <span className="text-info">{item.masters || 'Unknown'}</span>
                              <span className="me-1">, </span>
                            </>
                          ) : null}
                        </td>
                      )}
                      <td className="text-center">
                        <Icon icon="save" color="#000" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
        </Modal.Body>
      </Modal>
      <div
        className="row mt-5 bg-light p-5 border border-dark d-flex align-items-center citation-items-container"
      >
        {citations && citations.length > 0 ? (
          citations.map(c => (
            <div className="citation-item col-md-3">
              <div className="position-relative citation-item-content">
                <div className="citation-icon-container">
                  <Icon icon={citationIcon(c)} color="rgba(0,0,0,0.3)" size="2x" />
                </div>
                <span className="ms-2 citation-content-display">{citationDisplayMessage(c)}</span>
                <button type="button" className="delete-button" onClick={() => removeCitation(c)}>
                  x
                </button>
              </div>
            </div>
          ))
        ) : (
          <span className="text-center">No citations has been added yet.</span>
        )}
      </div>
      <Button onClick={() => setIsModalShown(true)} variant="dark" className="btn-block w-100 mt-3">
        <Icon icon="plus" />
      </Button>
    </div>
  );
};

CitationEditor.propTypes = {
  onChange: PropTypes.func,
  initialCitations: PropTypes.array,
};

CitationEditor.defaultProps = {
  onChange: undefined,
  initialCitations: [],
};

export default CitationEditor;
